import { find, replace } from "lodash";
import { useMemo } from "react";
import {
    DigitalWaiver,
    ExperiencePurchaseItem,
    Guest,
    Product,
    Waiver,
    WaiverExperiencePreference,
    WaiverParticipant,
    WaiverTemplateConfiguration,
} from "types";
import { isMinor } from "./User";
import { xolaWaiversApi } from "@/api/waiver";
import { useCollection } from "@/hooks/useCollection";

export const getWaiverUrl = (waiver: Waiver) => `/waivers/${waiver.id}/url`;

export const getWaiverFileName = (waiver: Waiver) => `Waiver-${waiver.id}.pdf`;

export const generateWaiverSignInURL = (
    waiverPreference: WaiverExperiencePreference,
    purchaseId: ID,
    purchaseItem: ExperiencePurchaseItem,
) => {
    if (purchaseItem?.waiverUrl) {
        return replace(purchaseItem.waiverUrl, "#", "");
    }

    // Replace placeholders with actual values from the purchase and purchase item
    return waiverPreference.parameterizedUrl
        .replace("{{ order.id }}", purchaseId)
        .replace("{{ purchase.id }}", purchaseId)
        .replace("{{ item.id }}", purchaseItem.id)
        .replace("{{ item.shortCode }}", purchaseItem.shortCode);
};

export const isProductHasLinkedWaiverTemplate = (waiverTemplates: DigitalWaiver[], product: Product) => {
    return waiverTemplates.some((waiverTemplate) => {
        return product?.waiverPreference?.remoteId === waiverTemplate.id;
    });
};

export const getLinkedWaiver = (waiverId?: ID, waivers?: Waiver[]) => {
    const linkedPurchaseWaivers = waivers ?? [];
    // Waiver linked to given Guest level
    return find(linkedPurchaseWaivers, { id: waiverId });
};

export const getLinkedWaiverParticipant = (waiverId?: ID, participantId?: ID, waivers?: Waiver[]) => {
    const linkedWaiver = getLinkedWaiver(waiverId, waivers);
    return find(linkedWaiver?.participants, { id: participantId });
};

export const getLinkedGuest = (guests: Guest[], participantId: ID) => {
    return guests.find((guest) => guest.participant === participantId);
};

export const isParticipantMinor = (participant: WaiverParticipant, minorAgeLimit?: number) => {
    return !!participant.dateOfBirth && isMinor(participant.dateOfBirth, minorAgeLimit);
};

export const useIsParticipantMinor = (participant: WaiverParticipant, waiverTypeId: ID): boolean => {
    const { data: waiverTemplateConfigurations } = useCollection<WaiverTemplateConfiguration>(
        "templates",
        {},
        { api: xolaWaiversApi },
    );

    const minorAgeLimit = useMemo(() => {
        if (!waiverTemplateConfigurations) return 18;

        return waiverTemplateConfigurations?.find((config) => config.id === waiverTypeId)?.adultAge ?? 18;
    }, [waiverTypeId, waiverTemplateConfigurations]);

    return isParticipantMinor(participant, minorAgeLimit);
};
